// M4 workstream C (world): shared helpers — lazy state, gates, domicile, small lookups. No top-level use of imported
// sim bindings (circular-import safe: production.js / m3 / m2 hunks import the world modules).
import { yf, fieldDef, eqDef, stat } from '../defs.js';
import { M4_FROM } from '../../data/m4/rules.js';
import { dayToDate } from '../../core/time.js';

export const m4On = (state) => yf(state) >= M4_FROM;

// Lazy, JSON-safe world state. Never created before 1 Jan 2000 (callers gate on m4On), so M1–M3 saves are unchanged.
export function W(state) {
  const w = (state.m4w ??= {});
  w.permits ??= {};      // `${fieldId}:${plot}` -> { filedDay, readyDay, regime }
  w.moratoria ??= {};    // regionId -> { from, until, minWaterFt, source } (dynamic: the player's own big spill)
  w.blowouts ??= {};     // wellId -> deepwater blowout liability record
  w.mega ??= {};         // megaproject id -> share record
  w.megaNews ??= {};     // megaproject id -> { phase, day } (news overrides, kept for every game)
  w.nat ??= {};          // M4 nationalization id -> { day, choice, ... }
  w.pesos ??= {};        // regionId -> trapped local-currency balance ($ value)
  w.events ??= {};       // freeze / wildfire / storms bookkeeping
  w.oilSands ??= {};     // fieldId -> mining / SAGD bookkeeping
  w.fx ??= {};           // informational news effects other workstreams read (opecQuota, takeaway, seismicity, sanctions)
  w.regimesSeen ??= {};  // regime id -> day first in force (notices)
  w.stats ??= {};
  return w;
}
export const peekW = (state) => state.m4w ?? null;

// Company domicile: 'us' by default (DESIGN: an American independent). Western = anyone who is not a sanctioned or
// state-aligned domicile — every player company today.
export const domicile = (state) => state.company?.domicile ?? 'us';
export const isUsCo = (state) => domicile(state) === 'us';
const NON_WESTERN = new Set(['ru', 'cn', 'ir', 've', 'kz']);
export const isWestern = (state) => !NON_WESTERN.has(domicile(state));

export const regionOf = (fieldId) => fieldDef(fieldId)?.regionId ?? null;
export const month = (state) => dayToDate(state.day).month;
export const ownWell = (w) => !!w && !w.ownerOperatorId;
export const GONE = new Set(['SEIZED', 'PLUGGED', 'HANDED_OVER', 'DRY', 'ABANDONED']);

// Items of `defId` located at a field (loc field) — oil-sands plants, diluent contracts, SWD etc.
export function itemsAt(state, fieldId, pred) {
  const out = [];
  for (const k in state.items) {
    const it = state.items[k];
    if (it.loc?.type === 'field' && it.loc.id === fieldId && pred(it, eqDef(it.defId))) out.push(it);
  }
  return out;
}
export const ownsAnywhere = (state, defId) => Object.values(state.items ?? {}).some((i) => i.defId === defId);
export const statOf = (defId, key, dflt) => stat(eqDef(defId), key, dflt);

// Deterministic per-key uniform in [0,1) without touching the main RNG (hash of seed + key).
export function hashU(state, key) {
  let h = 2166136261 ^ ((state.seed ?? 0) >>> 0);
  for (let i = 0; i < key.length; i++) { h ^= key.charCodeAt(i); h = Math.imul(h, 16777619); }
  h ^= h >>> 13; h = Math.imul(h, 0x5bd1e995); h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}
// Standard normal from two hashed uniforms (Box–Muller).
export function hashNormal(state, key) {
  const u = Math.max(1e-9, hashU(state, key + ':a')), v = hashU(state, key + ':b');
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}
